/**
 * MvbRecoveryView (P366) -- the MVB recovery sequence (gap-analysis F2).
 * Route: /twin/mvb-recovery.
 *
 * The ordered restoration plan for the declared MVB: waves, the
 * critical path, per-IBS time-to-viability vs declared tolerance, and
 * the stated assumptions -- the regime no longer ends at the
 * declaration.
 */
import { said } from "shared/i18n/said";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { api } from "shared/api/client";
import { MarsadIcon } from "shared/components/MarsadIcon";

interface RecoveryStepOut {
  wave: number;
  names: string[];
  /* Round 9 F1 -- the addresses of the services a step restores.
     `plan_to_dict` has sent these since P370 and nothing read them, so
     every wave named services a reader could not open. */
  service_ids: string[];
  duration_hours: number;
  starts_at_hours: number;
  finishes_at_hours: number;
  unrated: string[];
  on_critical_path: boolean;
  horizon: string;
  teams: string[];
  contention_delay_hours: number;
}

interface IBSViabilityOut {
  code: string;
  name: string;
  viable_at_hours: number;
  impact_tolerance_hours: number | null;
  verdict: "within" | "breached" | "unknown";
  horizon: string;
  horizon_source: "declared" | "derived";
  horizon_deadline_hours: number;
  meets_horizon: boolean;
}

interface HorizonSummaryOut {
  horizon: string;
  deadline_hours: number;
  members: number;
  worst_viable_at_hours: number;
  met: boolean;
}

interface TeamRow {
  team_id: string;
  name: string;
  headcount: number | null;
  max_concurrent_restorations: number | null;
  mvb_services: number;
  peak_concurrent_demand: number;
  bottleneck: boolean;
  /** Round 20 V9 -- a team with no one in it. */
  unstaffed?: boolean;
}

interface FacilityRow {
  facility_id: string;
  name: string;
  facility_type: string | null;
  mvb_services: number;
  share_pct: number | null;
  is_recovery_site: boolean;
}

interface ResourcesOut {
  teams: TeamRow[];
  facilities: FacilityRow[];
  unstaffed_services: string[];
  unsited_services: string[];
}

interface RecoveryPlanOut {
  generated_at: string;
  time_to_minimum_viability_hours: number;
  services_total: number;
  unrated_total: number;
  cycles_condensed: number;
  assumptions: string[];
  /* Round 9 G -- the same four, as keys the reader's catalogue can
     reach. `assumptions` stays: it is what a signed pack carries and
     the fallback if a key is ever missing. */
  assumption_keys?: { key: string; params: Record<string, string | number> }[];
  critical_path: string[];
  horizons: HorizonSummaryOut[];
  steps: RecoveryStepOut[];
  ibs_viability: IBSViabilityOut[];
}

const HORIZON_LABEL: Record<string, string> = {
  t_plus_4h: "T+4h",
  t_plus_24h: "T+24h",
  t_plus_72h: "T+72h",
  t_plus_1w: "T+1w",
};

const VERDICT_CLASS: Record<string, string> = {
  within: "bg-emerald-100 text-emerald-800",
  breached: "bg-rose-100 text-rose-800",
  unknown: "bg-slate-100 text-slate-600",
};

export function MvbRecoveryView() {
  const { t } = useTranslation();
  const plan = useQuery({
    queryKey: ["twin", "mvb-recovery"],
    queryFn: () => api.get<RecoveryPlanOut>("/v1/twin/mvb/recovery-sequence"),
  });
  const resources = useQuery({
    queryKey: ["twin", "mvb-resources"],
    queryFn: () => api.get<ResourcesOut>("/v1/twin/mvb/resources"),
  });
  const p = plan.data;
  const res = resources.data;

  return (
    <article data-testid="mvb-recovery-view" className="max-w-5xl space-y-5">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon name="mvb" className="me-2 inline-block h-6 w-6 align-[-4px]" />
          {t("mvbRecovery.heading")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">{t("mvbRecovery.body")}</p>
      </header>

      {plan.isError && (
        <p data-testid="mvb-recovery-error" className="text-sm text-rose-700">
          {t("mvbRecovery.error")}
        </p>
      )}

      {p && (
        <>
          <section
            data-testid="mvb-recovery-headline"
            className="flex flex-wrap items-baseline gap-4 rounded-xl border border-paper-2 bg-white p-4 shadow-card"
          >
            <div>
              <p className="font-mono text-[11px] uppercase tracking-wide text-brand-brassDark">
                {t("mvbRecovery.headline")}
              </p>
              <p className="font-display text-3xl text-slate-900">
                {t("screens.mvbRecoveryView.t")}
                {p.time_to_minimum_viability_hours}
                {t("screens.mvbRecoveryView.h")}
              </p>
            </div>
            <p className="text-sm text-slate-600">
              {t("mvbRecovery.summary", {
                services: p.services_total,
                waves: new Set(p.steps.map((s) => s.wave)).size,
                cycles: p.cycles_condensed,
                unrated: p.unrated_total,
              })}
            </p>
          </section>

          {/* Round 9 F1 -- where this plan goes next.
              This figure is PROJECTED from rated recovery objectives.
              The drill is what tests it against observed ones, and
              P370 built it precisely so the number here could be
              checked rather than believed. Neither screen pointed at
              the other, so the projection and its proof sat one route
              apart with nothing between them. */}
          <p data-testid="mvb-recovery-next" className="text-xs text-ink-2">
            {t("mvbRecovery.projectedNote")}{" "}
            <Link
              to="/twin/mvb-drill"
              data-testid="mvb-recovery-to-drill"
              className="text-brand-brassDark hover:underline"
            >
              {t("mvbRecovery.toDrill")}
            </Link>
          </p>
          <p className="text-xs text-ink-2">
            <Link
              to="/twin/mvb-target-state"
              data-testid="mvb-recovery-to-target"
              className="text-brand-brassDark hover:underline"
            >
              {t("mvbRecovery.toTargetState")}
            </Link>
          </p>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              {t("mvbRecovery.phases")}
            </h2>
            <ul
              data-testid="mvb-recovery-phases"
              className="flex flex-wrap gap-2 text-sm"
            >
              {p.horizons.map((h) => (
                <li
                  key={h.horizon}
                  className={`rounded border px-3 py-2 ${
                    h.met
                      ? "border-emerald-200 bg-emerald-50"
                      : "border-rose-200 bg-rose-50"
                  }`}
                >
                  <span className="font-medium text-slate-900">
                    {said(
                      t,
                      `enumWords.horizon.${h.horizon}`,
                      HORIZON_LABEL[h.horizon] ?? h.horizon,
                    )}
                  </span>{" "}
                  <span className="text-slate-600">
                    {t("mvbRecovery.phaseMembers", { count: h.members })}
                  </span>{" "}
                  <span className="font-mono text-xs tabular-nums text-slate-500">
                    {t("mvbRecovery.phaseWorst", {
                      worst: h.worst_viable_at_hours,
                      deadline: h.deadline_hours,
                    })}
                  </span>{" "}
                  <span
                    className={`font-semibold ${h.met ? "text-emerald-700" : "text-rose-700"}`}
                  >
                    {h.met ? t("mvbRecovery.met") : t("mvbRecovery.missed")}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              {t("mvbRecovery.viability")}
            </h2>
            <table
              data-testid="mvb-recovery-viability"
              className="w-full border-collapse text-sm"
            >
              <thead>
                <tr className="font-mono text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="py-1 text-start">{t("mvbRecovery.ibs")}</th>
                  <th className="text-start">{t("mvbRecovery.horizon")}</th>
                  <th className="text-end">{t("mvbRecovery.viableAt")}</th>
                  <th className="text-end">{t("mvbRecovery.tolerance")}</th>
                  <th className="text-end">{t("mvbRecovery.verdict")}</th>
                </tr>
              </thead>
              <tbody>
                {p.ibs_viability.map((r) => (
                  <tr key={r.code} className="border-t border-paper-2">
                    <td className="py-1">{r.name}</td>
                    <td>
                      <span
                        className={`rounded px-1.5 py-0.5 text-xs font-semibold ${
                          r.meets_horizon
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-rose-100 text-rose-800"
                        }`}
                      >
                        {said(
                          t,
                          `enumWords.horizon.${r.horizon}`,
                          HORIZON_LABEL[r.horizon] ?? r.horizon,
                        )}
                      </span>{" "}
                      <span className="text-xs text-slate-400">
                        {r.horizon_source === "declared"
                          ? t("mvbRecovery.declared")
                          : t("mvbRecovery.derived")}
                      </span>
                    </td>
                    <td className="text-end font-mono tabular-nums">
                      {t("screens.mvbRecoveryView.t")}
                      {r.viable_at_hours}
                      {t("screens.mvbRecoveryView.h")}
                    </td>
                    <td className="text-end font-mono tabular-nums">
                      {r.impact_tolerance_hours !== null
                        ? `${r.impact_tolerance_hours}h`
                        : "—"}
                    </td>
                    <td className="text-end">
                      <span
                        className={`rounded px-1.5 py-0.5 text-xs font-semibold ${VERDICT_CLASS[r.verdict]}`}
                      >
                        {r.verdict}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {res && res.teams.length > 0 && (
            <section data-testid="mvb-resources" className="space-y-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                {t("mvbRecovery.resources")}
              </h2>
              <ul className="flex flex-wrap gap-2 text-sm">
                {res.teams.map((tm) => (
                  <li
                    key={tm.team_id}
                    className={`rounded border px-3 py-2 ${
                      tm.bottleneck || tm.unstaffed
                        ? "border-rose-200 bg-rose-50"
                        : "border-paper-2 bg-white"
                    }`}
                  >
                    <span className="font-medium text-slate-900">{tm.name}</span>{" "}
                    <span className="font-mono text-xs tabular-nums text-slate-600">
                      {t("mvbRecovery.teamDemand", {
                        services: tm.mvb_services,
                        demand: tm.peak_concurrent_demand,
                        capacity: tm.max_concurrent_restorations ?? "—",
                      })}
                    </span>
                    {/* Round 12 H5 -- "...vs capacity 1bottleneck". The
                        flag that says this team is the constraint was
                        glued to the number it constrains. */}
                    {tm.unstaffed && (
                      <span
                        data-testid={`mvb-team-unstaffed-${tm.team_id}`}
                        className="block font-semibold text-rose-700"
                      >
                        {t("mvbRecovery.unstaffed", { name: tm.name })}
                      </span>
                    )}
                    {tm.bottleneck && (
                      <>
                        {" \u00b7 "}
                        <span className="ms-1 font-semibold text-rose-700">
                          {t("mvbRecovery.bottleneck")}
                        </span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
              <ul className="flex flex-wrap gap-2 text-xs text-slate-600">
                {res.facilities.map((f) => (
                  <li
                    key={f.facility_id}
                    className="rounded border border-paper-2 bg-white px-2 py-1"
                  >
                    {f.name}
                    {f.is_recovery_site ? t("screens.mvbRecoveryView.dr") : ""} —{" "}
                    {t("mvbRecovery.siteShare", {
                      services: f.mvb_services,
                      pct: f.share_pct ?? 0,
                    })}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              {t("mvbRecovery.sequence")}
            </h2>
            <ol data-testid="mvb-recovery-steps" className="space-y-1">
              {p.steps.map((s, i) => (
                <li
                  key={i}
                  className={`flex flex-wrap items-baseline gap-x-3 rounded border px-3 py-2 text-sm ${
                    s.on_critical_path
                      ? "border-brand-brass/50 bg-brand-brass/5"
                      : "border-paper-2 bg-white"
                  }`}
                >
                  <span className="font-mono text-xs text-slate-400">
                    {t("screens.mvbRecoveryView.w")}
                    {s.wave}
                  </span>
                  {/* Round 9 F1 -- each service in the wave, openable.
                      This screen has never been walked by any persona
                      in nine rounds, and it is where the Resilience
                      Lead would prove the set she has just signed. It
                      named thirteen services and led nowhere; a plan
                      you cannot follow into is a document, not a
                      surface. */}
                  <span className="font-medium text-slate-900">
                    {s.names.map((n, j) => (
                      <span key={n}>
                        {j > 0 && " + "}
                        {s.service_ids[j] ? (
                          <Link
                            to={`/twin/service/${s.service_ids[j]}`}
                            data-testid={`mvb-recovery-step-service-${s.service_ids[j]}`}
                            className="text-brand-brassDark hover:underline"
                          >
                            {n}
                          </Link>
                        ) : (
                          n
                        )}
                      </span>
                    ))}
                  </span>
                  {s.names.length > 1 && (
                    <span className="rounded bg-amber-100 px-1.5 text-xs font-medium text-amber-800">
                      {t("mvbRecovery.cycleBlock")}
                    </span>
                  )}
                  <span className="font-mono text-xs text-slate-500 tabular-nums">
                    {t("screens.mvbRecoveryView.t")}
                    {s.starts_at_hours}
                    {t("screens.mvbRecoveryView.hT")}
                    {s.finishes_at_hours}
                    {t("screens.mvbRecoveryView.h")}
                  </span>
                  {s.contention_delay_hours > 0 && (
                    <span className="rounded bg-rose-100 px-1.5 text-xs text-rose-800">
                      {t("mvbRecovery.waited", {
                        hours: s.contention_delay_hours,
                        team: s.teams.join(", "),
                      })}
                    </span>
                  )}
                  {s.unrated.length > 0 && (
                    <span className="text-xs text-amber-700">
                      {t("mvbRecovery.unrated", {
                        count: s.unrated.length,
                      })}
                    </span>
                  )}
                  <span className="rounded bg-slate-100 px-1.5 text-xs text-slate-600">
                    {said(
                      t,
                      `enumWords.horizon.${s.horizon}`,
                      HORIZON_LABEL[s.horizon] ?? s.horizon,
                    )}
                  </span>
                  {s.on_critical_path && (
                    <span className="ms-auto font-mono text-[10px] uppercase tracking-wide text-brand-brassDark">
                      {t("mvbRecovery.critical")}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </section>

          <section className="space-y-1">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              {t("mvbRecovery.assumptions")}
            </h2>
            <ul className="list-disc ps-5 text-xs text-slate-500">
              {/* Round 9 G -- the plan's stated LIMITS, which is the
                  part a supervisor reads closely, and they were
                  composed server-side so they stayed English under
                  any translation. Falls back to the composed English
                  where a key is absent: a missing translation should
                  read as English, not vanish. */}
              {(p.assumption_keys?.length
                ? p.assumption_keys.map((a) => ({
                    id: a.key,
                    text: t(a.key, a.params),
                  }))
                : p.assumptions.map((a) => ({ id: a, text: a }))
              ).map((a) => (
                <li key={a.id}>{a.text}</li>
              ))}
            </ul>
          </section>
        </>
      )}
    </article>
  );
}
